import { Range, Root, Thumb, Track } from '@radix-ui/react-slider';
import classNames from 'classnames';
import { useState } from 'react';
import { TextBase, TextCaption } from '../Typography';

export interface SliderProps {
  onChange?: (value: number[]) => void;
  onValueCommit?: (value: number[]) => void;
  title?: string;
  min?: number;
  max?: number;
  defaultValue?: number;
  step?: number;
  valueDescription?: string;
  showMinMax?: boolean;
  minLabel?: string;
  maxLabel?: string;
  showValueOnThumb?: boolean;
}

export const Slider = ({
  min = 0,
  max = 100,
  defaultValue = 0,
  onChange,
  step = 1,
  title,
  valueDescription = '',
  showMinMax = false,
  minLabel,
  maxLabel,
  showValueOnThumb = true,
  onValueCommit,
}: SliderProps) => {
  const [value, setValue] = useState(defaultValue);
  return (
    <div className="flex flex-col gap-6 w-full">
      {title && (
        <TextBase
          label={title}
          color="text-ds-natural-600"
        />
      )}
      <div className="flex flex-col justify-center items-center">
        <Root
          name="slider"
          className={classNames(
            'relative flex items-center select-none touch-none h-6 justify-center w-full'
          )}
          value={[value]}
          max={max}
          min={min}
          onValueCommit={(v) => {
            setValue(v[0]);
            if (onValueCommit) {
              onValueCommit(v);
            }
          }}
          onValueChange={(v) => {
            setValue(v[0]);
            if (onChange) {
              onChange(v);
            }
          }}
          step={step}
        >
          <Track
            className="relative grow rounded-full h-1.5 bg-ds-accent3-300 flex justify-between w-full"
            data-testid="sliderTrack"
          >
            {showMinMax && (
              <div className="pt-2.5">
                <TextCaption label={minLabel ?? min} />
              </div>
            )}
            <Range className="absolute bg-ds-primary-300 rounded-full h-full" />
            {showMinMax && (
              <div className="pt-2.5">
                <TextCaption label={maxLabel ?? max} />
              </div>
            )}
          </Track>
          <Thumb className="focus:outline-none">
            <div className="flex justify-center items-center">
              <div
                className="w-4 h-4 bg-ds-primary-300 rounded-full"
                data-testid={`slider-${title || ''}thumb`}
              />
              {showValueOnThumb && (
                <div className="absolute -top-7">
                  <TextCaption label={`${value}${valueDescription}`} />
                </div>
              )}
            </div>
          </Thumb>
        </Root>
      </div>
    </div>
  );
};
